/**
 * Provides SSE connection state to the component tree.
 *
 * Wires useEventStream (connection lifecycle) to useRealtimeInvalidation
 * (event → cache ops). Exposes `isConnected` so individual query hooks
 * can decide whether to fall back to polling.
 *
 * Mount inside AuthProvider (needs auth context) and below
 * QueryClientProvider (needs query client).
 */

import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useEventStream, type ConnectionState } from '@hooks/useEventStream';
import { useRealtimeInvalidation } from '@hooks/useRealtimeInvalidation';
import { useAuth } from '@hooks/useAuth';
import { AuthSessionState } from '@providers/AuthProvider';
import { toast } from '@hooks/use-toast';
import { EventStreamContext, type EventStreamContextValue } from './eventStreamContext';

interface EventStreamProviderProps {
  children: ReactNode;
}

export function EventStreamProvider({ children }: EventStreamProviderProps) {
  // Gate SSE on the post-tolerance AUTHENTICATED state, not raw `isAuthenticated`.
  // The auth state machine has a 1500ms tolerance window for transient SDK
  // refresh blips (see AuthProvider.tsx:117-125) — `isAuthenticated` flips on
  // the synchronous edge and would tear down + reconnect SSE on every blip,
  // emitting a spurious FULL_SYNC each time. Driving off `sessionState` keeps
  // the connection up across the tolerance window.
  //
  // Also gate on `user?.id`: AUTHENTICATED can flip true before the backend
  // `User` row exists (Logto auth completes before signup creates the row),
  // and SSE goes terminally fatal on a 4xx with no automatic recovery.
  const { sessionState, user } = useAuth();
  const enabled = sessionState === AuthSessionState.AUTHENTICATED && Boolean(user?.id);
  const handleEvent = useRealtimeInvalidation();

  const { connectionState } = useEventStream({
    enabled,
    onEvent: handleEvent,
  });

  // Surface the terminal-disconnect state to the user. `'fatal'` fires when
  // SSE hits a non-retriable 4xx (other than the 401 retry budget) and
  // stops reconnecting entirely. webapp#471 removed the SSE-layer polling
  // fallback for realtime invalidation, so without this signal the user has
  // no idea their feed has stopped receiving live updates. Per-hook polling
  // fallbacks (useUnreadCount, useNotificationCount, useChat,
  // useNewMeetversePostsIndicator) still re-enable when `isConnected` flips
  // false, so some surfaces continue to refresh on their own cadence.
  // The hook self-recovers on foreground via the appStateChange handler.
  // A finite-duration toast (vs a persistent badge) is the cheap-fold-in
  // shape; #682 tracks the persistent connection-status surface called
  // for in the original plan.
  //
  // Gate on the transition into `'fatal'` (not the state itself) so the
  // toast fires once per real disconnect. Without this, React StrictMode's
  // double-effect-invocation in dev would show two toasts on every entry,
  // and any future remount path could fire it on stale state.
  const previousStateRef = useRef<ConnectionState>(connectionState);
  useEffect(() => {
    const wasNotFatal = previousStateRef.current !== 'fatal';
    previousStateRef.current = connectionState;
    if (connectionState === 'fatal' && wasNotFatal) {
      toast({
        title: 'Live updates paused',
        description: 'Reload to catch up.',
        variant: 'error',
        duration: 10000,
      });
    }
  }, [connectionState]);

  const value = useMemo<EventStreamContextValue>(
    () => ({
      connectionState,
      isConnected: connectionState === 'connected',
    }),
    [connectionState],
  );

  return <EventStreamContext.Provider value={value}>{children}</EventStreamContext.Provider>;
}
